import {
  GET_PROJECT,
  GET_PROJECTS_REQUEST,
  GET_PROJECTS_SUCCESS,
  GET_PROJECTS_ERROR,
  UPDATE_PROJECT_REQUEST,
  UPDATE_PROJECT_SUCCESS,
  UPDATE_PROJECT_ERROR,
  DELETE_PROJECT,
  GET_PROJECTS_SCENARIO_REQUEST,
  GET_PROJECTS_SCENARIO_SUCCESS,
  GET_PROJECTS_SCENARIO_ERROR,
  CLEAR_PROJECTS_SCENARIO,
  CREATE_PROJECT_REQUEST,
  CREATE_PROJECT_SUCCESS,
  CREATE_PROJECT_ERROR,
  GET_PROJECT_WITH_COLLABS_AND_ROLES_SUCCESS,
  UPDATE_PROJECT_COLLABORATORS,
  GET_PROJECTS_DASHBOARD_REQUEST,
  GET_PROJECTS_DASHBOARD_SUCCESS,
  GET_PROJECTS_DASHBOARD_ERROR,
  GET_ACTIVE_PROJECTS_REQUEST,
  GET_ACTIVE_PROJECTS_SUCCESS,
  GET_ACTIVE_PROJECTS_ERROR,
  GET_PROGRAMS_REQUEST,
  GET_PROGRAMS_SUCCESS,
  GET_PROGRAMS_ERROR,
  UPDATE_PROJECTS_ROW_INDEX_REQUEST,
  UPDATE_PROJECTS_ROW_INDEX_SUCCESS,
  UPDATE_PROJECTS_ROW_INDEX_ERROR,
  CREATE_PROJECT_WITH_PROGRAM_SUCCESS,
  DELETE_PROGRAM,
  MARK_PROJECTS_DELETING,
  UNMARK_PROJECT_DELETING,
  APPEND_PROJECTS_TO_PROGRAM,
  CREATE_PROGRAM,
  GET_ALL_EXISTING_SUB_CODES,
  UPDATE_CLOSED_STATUS_OF_SUB_CODES,
  SET_ACTIVE_PROJECT,
  ADD_PROGRAM_IF_DOES_NOT_EXIST,
  ADD_PROJECT,
  PROPAGATE_PROJECT_ROLE,
  UPDATE_PROJECT_WORK_PACKAGES,
  UPDATE_PROJECT_WORK_PACKAGES_REQUEST,
  UPDATE_PROJECT_WORK_PACKAGES_ERROR,
  APPROVE_PROJECT_SCENARIO,
  REJECT_PROJECT_SCENARIO,
  GET_MULTI_PROJECTS_INLINE_DATA_REQUEST,
  GET_MULTI_PROJECTS_INLINE_DATA_SUCCESS,
  GET_MULTI_PROJECTS_INLINE_DATA_ERROR,
  RESET_MULTI_PROJECTS_INLINE_DATA,
  UPDATE_PROJECTS,
  RESET_METRICS_AFTER_REJECTION_OF_SCENARIOS,
} from "../types";
import produce from "immer";
import { updateItemInArray } from "../../utils/FilterUtils/FilterUtils";
import { extractFieldWithRecursion } from "../../utils/ObjectUtils/ObjectUtils";

const DEFAULT_PROJECT_DASHBOARD_STAT = { value: 0, percentage: 0 };
const DASHBOARD_PROJECT_KEYS = [
  "activeProjects",
  "overBudgetProjects",
  "staleProjects",
];
const DEFAULT_DASHBOARD_VALUE = {
  allProjects: 0,
  activeProjects: DEFAULT_PROJECT_DASHBOARD_STAT,
  overBudgetProjects: DEFAULT_PROJECT_DASHBOARD_STAT,
  staleProjects: DEFAULT_PROJECT_DASHBOARD_STAT,
}

function normalizeId(value) {
  return value == null ? null : String(value);
}

function isSameId(left, right) {
  const leftNormalized = normalizeId(left);
  const rightNormalized = normalizeId(right);
  if (leftNormalized == null || rightNormalized == null) return false;
  return leftNormalized === rightNormalized;
}

function removeProjectIdsFromDashboard(dashboard, removedProjectIds) {
  if (!dashboard || typeof dashboard !== "object") return dashboard;
  const removed = new Set(
    (removedProjectIds || [])
      .filter((id) => id != null)
      .map((id) => String(id))
  );
  if (!removed.size) return dashboard;

  const updated = { ...dashboard };
  let allProjects = Number.isFinite(dashboard.allProjects) ? dashboard.allProjects : 0;
  allProjects = Math.max(0, allProjects - removed.size);

  DASHBOARD_PROJECT_KEYS.forEach((key) => {
    const current = dashboard[key] || DEFAULT_PROJECT_DASHBOARD_STAT;
    const next = { ...current };
    if (Array.isArray(current.projectsIds)) {
      const filteredIds = current.projectsIds.filter(
        (id) => !removed.has(String(id))
      );
      next.projectsIds = filteredIds;
      next.value = filteredIds.length;
    } else if (typeof current.value === "number") {
      next.value = Math.max(0, current.value);
    }
    next.percentage =
      allProjects > 0 && Number.isFinite(next.value)
        ? (next.value / allProjects) * 100
        : 0;
    updated[key] = next;
  });

  updated.allProjects = allProjects;
  return updated;
}

const initialState = {
  items: [],
  roles: [],
  collaborators: [],
  permissions: [],
  activeItems: [],
  item: {},
  dashboard: DEFAULT_DASHBOARD_VALUE,
  programs: [],
  projectsScenario: {},
  isFetchingTimeline: false,
  isFetchingDashboard: false,
  isUpdating: false,
  isFetching: false,
  isActiveItemsFetching: false,
  isProgramsFetching: false,
  selectedProjects: {
    isFetching: false,
    isUpdating: false,
    inlineData: {},
    error: null,
  },
  allSubCodes: [],
  error: "",
};
// eslint-disable-next-line
export default function (state = initialState, { type, payload }) {
  switch (type) {
    case CREATE_PROJECT_REQUEST:
      return {
        ...state,
        isFetching: true,
        error: "",
      };
    case CREATE_PROJECT_SUCCESS:
      return {
        ...state,
        items: [...state.items, payload],
        isFetching: false,
        error: "",
      };
    case CREATE_PROJECT_ERROR:
      return {
        ...state,
        error: payload,
        isFetching: false,
      };
    case CREATE_PROGRAM:
      return {
        ...state,
        items: [...state.items, payload],
        programs: [...state.programs, payload],
        isFetching: false,
        error: "",
      };
    case CREATE_PROJECT_WITH_PROGRAM_SUCCESS:
      return produce(state, (draftState) => {
        const addedProgram = payload[0];
        draftState.isFetching = false;
        draftState.items = [...draftState.items, ...payload];
        draftState.programs = [...draftState.programs, addedProgram];
        draftState.error = "";
      });
    case ADD_PROGRAM_IF_DOES_NOT_EXIST:
      const programAlreadyExists = state.programs.some(
        (program) => program.id === payload?.id
      );
      // not mutating the state if program already exists
      if (programAlreadyExists) return state;

      return produce(state, (draftState) => {
        const newItems = draftState.items.slice();
        const newPrograms = draftState.programs.slice();
        newItems.push(payload);
        newPrograms.push(payload);
        draftState.items = newItems;
        draftState.programs = newPrograms;
      });
    case ADD_PROJECT:
      return produce(state, (draftState) => {
        const newItems = draftState.items.slice();
        newItems.push(payload);
        draftState.items = newItems;
      });
    case GET_PROJECTS_REQUEST:
      return produce(state, (draftState) => {
        draftState.isFetching = true;
        draftState.items = [];
        draftState.error = "";
      });
    case GET_PROJECTS_SUCCESS:
      return produce(state, (draftState) => {
        const mappedProjects = payload.data.map(
          (projectData) => {
            // Light payloads (/project/names: {id, name, currentScenarioId}) have no
            // wrapper `project` field — pass them through as-is.
            if (!projectData.start || !projectData.finish) return projectData.project || projectData;

            const { project, start, finish, ...rest } = projectData;
            project.start = new Date(start);
            project.finish = new Date(finish);
            return { ...project, ...rest };
          }
        );

        draftState.isFetching = false;
        draftState.error = "";
        draftState.items = mappedProjects;

        if (payload.currentProjectId) {
          draftState.item =
            mappedProjects.find((p) => p.id === +payload.currentProjectId) ||
            {};
        }
      });
    case UPDATE_PROJECTS:
      const updatedItems = payload.map(item => {
        const { project, start, finish, ...rest } = item;
        project.start = new Date(start);
        project.finish = new Date(finish);
        return { ...project, ...rest };
      })
      return produce(state, draftState => {
        draftState.items = draftState.items.map(item => {
          const matchingItem = updatedItems.find(i => i.id === item.id);
          return matchingItem ? matchingItem : item;
        })
      });
    case RESET_METRICS_AFTER_REJECTION_OF_SCENARIOS:
      return produce(state, draftState => {
        draftState.items = draftState.items.map(item => {
          if (payload.includes(item.id)) {
            item.scenarioRisk = null;
            item.scenarioRiskColor = null;
          }
          return item;
        })
      })
    case GET_PROJECTS_ERROR:
      return produce(state, (draftState) => {
        draftState.isFetching = false;
        draftState.error = payload;
      });
    case GET_PROJECT_WITH_COLLABS_AND_ROLES_SUCCESS:
      return {
        ...state,
        ...payload,
        items: payload.projects.map(({ project, start, finish, ...rest }) => {
          if (!start || !finish) return project;
          const startDate = new Date(start);
          const finishDate = new Date(finish);
          return { ...project, start: startDate, finish: finishDate, ...rest };
        }),
        isFetching: false,
      };
    case UPDATE_PROJECTS_ROW_INDEX_REQUEST:
      return produce(state, (draftState) => {
        draftState.isUpdating = true;
        draftState.error = "";
      });
    case UPDATE_PROJECTS_ROW_INDEX_SUCCESS:
      return produce(state, (draftState) => {
        draftState.isUpdating = false;
        payload.forEach((updatedItem) => {
          const index = draftState.items.findIndex(
            (item) => item.id === updatedItem.id
          );
          draftState.items[index] = updatedItem;
        });
      });
    case UPDATE_PROJECTS_ROW_INDEX_ERROR:
      return produce(state, (draftState) => {
        draftState.isUpdating = false;
        draftState.error = payload;
      });
    case APPEND_PROJECTS_TO_PROGRAM:
      return produce(state, (draftState) => {
        draftState.items = draftState.items.map((item) => {
          if (payload.projectIds.includes(item.id))
            item.programId = payload.programId;
          return item;
        });
      });
    case GET_ACTIVE_PROJECTS_REQUEST:
      return {
        ...state,
        isActiveItemsFetching: true,
        activeItems: [],
        error: "",
      };
    case GET_ACTIVE_PROJECTS_SUCCESS:
      return {
        ...state,
        activeItems: payload,
        isActiveItemsFetching: false,
      };
    case GET_ACTIVE_PROJECTS_ERROR:
      return {
        ...state,
        isActiveItemsFetching: false,
        error: payload,
      };
    case GET_PROJECTS_SCENARIO_REQUEST:
      return {
        ...state,
        isFetchingTimeline: true,
        projectsScenario: {},
        error: "",
      };
    case GET_PROJECTS_SCENARIO_SUCCESS:
      return {
        ...state,
        projectsScenario: payload,
        isFetchingTimeline: false,
      };
    case GET_PROJECTS_SCENARIO_ERROR:
      return {
        ...state,
        isFetchingTimeline: false,
        projectsScenario: {},
        error: payload,
      };
    case UPDATE_PROJECT_REQUEST:
      return produce(state, (draftState) => {
        draftState.isUpdating = true;
        draftState.error = "";
      });
    case UPDATE_PROJECT_SUCCESS:
      return produce(state, (draftState) => {
        draftState.isUpdating = false;
        draftState.items = updateItemInArray(draftState.items, payload);
      });
    case UPDATE_PROJECT_ERROR:
      return produce(state, (draftState) => {
        draftState.isUpdating = false;
        draftState.error = payload;
      });

    case UPDATE_PROJECT_COLLABORATORS:
      return produce(state, (draftState) => {
        draftState.items = draftState.items.map((project) =>
          project.id === payload.id ? { ...project, ...payload } : project
        );
      });
    case GET_ALL_EXISTING_SUB_CODES:
      return produce(state, (draftState) => {
        draftState.allSubCodes = payload;
      });
    case GET_PROJECT:
      return {
        ...state,
        item: payload,
      };
    case CLEAR_PROJECTS_SCENARIO:
      return {
        ...state,
        projectsScenario: {},
      };
    case DELETE_PROJECT:
      return produce(state, (draftState) => {
        const removedProjectIds = draftState.items
          .filter((project) => isSameId(project.id, payload) && project.typeOfProject)
          .map((project) => project.id);
        draftState.items = draftState.items.filter(
          (project) => !isSameId(project.id, payload)
        );
        draftState.dashboard = removeProjectIdsFromDashboard(
          draftState.dashboard,
          removedProjectIds
        );
      });
    case MARK_PROJECTS_DELETING:
      return produce(state, (draftState) => {
        const ids = new Set(payload || []);
        draftState.items.forEach((p) => {
          if (ids.has(p.id)) p._deleting = true;
        });
      });
    case UNMARK_PROJECT_DELETING:
      return produce(state, (draftState) => {
        draftState.items.forEach((p) => {
          if (isSameId(p.id, payload)) p._deleting = false;
        });
      });
    case DELETE_PROGRAM:
      return produce(state, (draftState) => {
        const removedProjectIds = draftState.items
          .filter(
            (item) => item.typeOfProject && isSameId(item.programId, payload)
          )
          .map((item) => item.id);
        draftState.items = draftState.items.filter((item) =>
          item.typeOfProject
            ? !isSameId(item.programId, payload)
            : !isSameId(item.id, payload)
        );
        draftState.programs = draftState.programs.filter(
          (program) => !isSameId(program.id, payload)
        );
        draftState.dashboard = removeProjectIdsFromDashboard(
          draftState.dashboard,
          removedProjectIds
        );
      });
    case GET_PROJECTS_DASHBOARD_REQUEST:
      return produce(state, (draftState) => {
        draftState.isFetchingDashboard = true;
      });
    case GET_PROJECTS_DASHBOARD_SUCCESS:
      return produce(state, (draftState) => {
        draftState.dashboard = payload;
        draftState.isFetchingDashboard = false;
      });
    case GET_PROJECTS_DASHBOARD_ERROR:
      return produce(state, (draftState) => {
        draftState.isFetchingDashboard = false;
        draftState.error = payload;
      });
    case GET_PROGRAMS_REQUEST:
      return {
        ...state,
        isProgramsFetching: true,
        programs: [],
        error: "",
      };
    case GET_PROGRAMS_SUCCESS:
      return {
        ...state,
        programs: payload,
        isProgramsFetching: false,
      };
    case GET_PROGRAMS_ERROR:
      return {
        ...state,
        isProgramsFetching: false,
        error: payload,
      };
    case UPDATE_CLOSED_STATUS_OF_SUB_CODES:
      return produce(state, (draftState) => {
        draftState.items = draftState.items.map((item) => {
          if (!item.typeOfProject) return item;

          item.workPackages.forEach((wp) => {
            if (wp.id in payload) {
              wp.closed = payload[wp.id];
            }
          });
          return item;
        });
      });
    case SET_ACTIVE_PROJECT:
      return produce(state, (draftState) => {
        draftState.item = draftState.items.find((item) => item.id === payload);
      });
    case PROPAGATE_PROJECT_ROLE:
      return produce(state, (draftState) => {
        draftState.items = draftState.items.map((item) => {
          if (item.id in payload) {
            item.projectCollaborators = {
              ...item.projectCollaborators,
              ...payload[item.id],
            };
          }
          return item;
        });
      });
    case UPDATE_PROJECT_WORK_PACKAGES_REQUEST:
      return produce(state, (draftState) => {
        draftState.isUpdating = true;
        draftState.error = "";
      });
    case UPDATE_PROJECT_WORK_PACKAGES:
      return produce(state, (draftState) => {
        draftState.items = draftState.items.map((item) => {
          if (item.id === payload.id) item.workPackages = payload.workPackages;
          return item;
        });
        draftState.isUpdating = false;
      });
    case UPDATE_PROJECT_WORK_PACKAGES_ERROR:
      return produce(state, (draftState) => {
        draftState.isUpdating = false;
        draftState.error = payload;
      });
    case APPROVE_PROJECT_SCENARIO:
      return produce(state, (draftState) => {
        draftState.items = draftState.items.map((item) => {
          if (item.id === payload.projectId) {
            item.currentScenarioId = payload.currentScenarioId;
          }
          return item;
        });
      });
    case REJECT_PROJECT_SCENARIO:
      return produce(state, (draftState) => {
        draftState.items = draftState.items.map((item) => {
          if (item.id === payload.projectId) {
            item.currentScenarioId = null;
          }
          return item;
        });
      });
    case GET_MULTI_PROJECTS_INLINE_DATA_REQUEST:
      return produce(state, draftState => {
        draftState.selectedProjects.isFetching = true;
      });
    case GET_MULTI_PROJECTS_INLINE_DATA_SUCCESS:
      return produce(state, draftState => {
        draftState.selectedProjects.isFetching = false;
        draftState.selectedProjects.inlineData = payload;
      });
    case GET_MULTI_PROJECTS_INLINE_DATA_ERROR:
      return produce(state, draftState => {
        draftState.selectedProjects.isFetching = false;
        draftState.selectedProjects.error = payload;
      });
    case RESET_MULTI_PROJECTS_INLINE_DATA:
      return produce(state, draftState => {
        draftState.selectedProjects.inlineData = {};
      })
    default:
      return state;
  }
}
